import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Search, RefreshCw, Filter, Download } from "lucide-react";

/**
 * Admin → Safety Acknowledgement Audit
 *
 * Lists users and their state for the "Never pay outside Cauldron" popup.
 * Filters: role (seeker/provider/all), status (all/acknowledged/pending),
 * free-text email/name search, includeTest toggle.
 *
 * Used during trust-and-safety reviews — e.g. after a fraud report, the
 * admin can immediately tell whether that user had agreed to the on-screen
 * warning at the time of the incident, and when.
 */
export default function AdminSafetyAudit() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ acknowledged: 0, pending: 0, ackRate: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");      // all | acknowledged | pending
  const [roleFilter, setRoleFilter] = useState("seeker");        // seeker | provider | all
  const [query, setQuery] = useState("");
  const [includeTest, setIncludeTest] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/safety-audit", {
        params: {
          status: statusFilter,
          role: roleFilter,
          q: query.trim(),
          limit: 500,
          includeTest,
        },
      });
      setRows(data.rows || []);
      setSummary(data.summary || { acknowledged: 0, pending: 0, ackRate: 0 });
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load safety audit");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter, query, includeTest]);

  useEffect(() => {
    // Debounce to avoid hammering the API while the operator is typing.
    const t = setTimeout(() => { fetchRows(); }, 300);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const exportCsv = () => {
    if (!rows.length) {
      toast.error("No rows to export");
      return;
    }
    const header = ["email", "name", "role", "acknowledged", "acknowledgedAt", "createdAt", "lastLoginAt"];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      const safe = (v) => (v == null ? "" : `"${String(v).replace(/"/g, '""')}"`);
      lines.push([
        safe(r.email), safe(r.name), safe(r.role),
        r.acknowledged ? "yes" : "no",
        safe(r.acknowledgedAt), safe(r.createdAt), safe(r.lastLoginAt),
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `safety-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-safety-audit">
      <div>
        <div className="label-mystic">Trust &amp; Safety</div>
        <h2 className="font-serif text-3xl mt-1">Safety Acknowledgement Audit</h2>
        <p className="text-foreground/60 text-sm mt-2 max-w-2xl">
          Tracks which users have explicitly confirmed the on-screen warning that they will{" "}
          <span className="text-secondary">never pay practitioners outside the app</span>.
          Use this when reviewing a fraud report — you can immediately see whether the affected
          user had acknowledged the warning, and exactly when.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total in view" value={total} testid="kpi-total" />
        <Kpi label="Acknowledged" value={summary.acknowledged} accent="emerald" testid="kpi-ack" />
        <Kpi label="Pending" value={summary.pending} accent="amber" testid="kpi-pending" />
        <Kpi label="Ack rate" value={`${summary.ackRate}%`} testid="kpi-rate" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-md border border-secondary/15 bg-secondary/[0.03]">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-[0.22em] text-foreground/60 mb-1">Status</label>
          <select
            data-testid="safety-audit-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-background border border-secondary/20 rounded px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="acknowledged">Acknowledged only</option>
            <option value="pending">Pending only</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-[0.22em] text-foreground/60 mb-1">Role</label>
          <select
            data-testid="safety-audit-role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-background border border-secondary/20 rounded px-3 py-2 text-sm"
          >
            <option value="seeker">Seekers</option>
            <option value="provider">Providers</option>
            <option value="all">All roles</option>
          </select>
        </div>

        <div className="flex flex-col flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-[0.22em] text-foreground/60 mb-1">Search email / name</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-foreground/40" />
            <input
              data-testid="safety-audit-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. @gmail.com or Sarah"
              className="w-full bg-background border border-secondary/20 rounded pl-7 pr-3 py-2 text-sm"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-foreground/70 cursor-pointer">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            data-testid="safety-audit-include-test"
          />
          Include test accounts
        </label>

        <button
          onClick={fetchRows}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded border border-secondary/30 bg-secondary/10 hover:bg-secondary/15 text-xs uppercase tracking-[0.22em] disabled:opacity-50"
          data-testid="safety-audit-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-secondary/30 hover:bg-secondary/10 text-xs uppercase tracking-[0.22em]"
          data-testid="safety-audit-export"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="rounded-md border border-secondary/15 overflow-x-auto" data-testid="safety-audit-table">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/[0.04] text-foreground/70 text-xs uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Acknowledged at</th>
              <th className="text-left px-4 py-3">Joined</th>
              <th className="text-left px-4 py-3">Last login</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/60" data-testid="safety-audit-loading">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/60" data-testid="safety-audit-empty">No users match the current filters.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-secondary/10 hover:bg-secondary/[0.03]">
                <td className="px-4 py-3">
                  <div className="font-medium">{r.name || "—"}</div>
                  <div className="text-foreground/50 text-xs">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-foreground/80 text-xs uppercase tracking-wider">{r.role}</td>
                <td className="px-4 py-3">
                  {r.acknowledged ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs" data-testid={`status-ack-${r.id}`}>
                      <ShieldCheck size={12} /> Acknowledged
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs" data-testid={`status-pending-${r.id}`}>
                      <ShieldAlert size={12} /> Pending
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-foreground/70">{fmt(r.acknowledgedAt)}</td>
                <td className="px-4 py-3 font-mono text-xs text-foreground/60">{fmt(r.createdAt)}</td>
                <td className="px-4 py-3 font-mono text-xs text-foreground/60">{fmt(r.lastLoginAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  const tone =
    accent === "emerald" ? "border-emerald-500/30 bg-emerald-500/[0.05]"
    : accent === "amber" ? "border-amber-500/30 bg-amber-500/[0.05]"
    : "border-secondary/20 bg-secondary/[0.04]";
  return (
    <div className={`p-4 rounded-md border ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/60">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return "—";
    return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
  } catch {
    return "—";
  }
}
